import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, FileText } from "lucide-react";
import { Link } from "react-router-dom";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import DynamicContactSection from "@/components/legal/DynamicContactSection";

const Terms = () => {
  const { data: settings } = useSiteSettings();

  // Get dynamic values with fallbacks
  const siteTitle = settings?.site_title || "Bobo & Teedee®";
  const legalEmail = settings?.legal_email || "legal@boboteedee.ky";
  const legalAddress = settings?.legal_address || "Bobo & Teedee® Legal Department, Grand Cayman, Cayman Islands";
  const effectiveDate = settings?.terms_effective_date 
    ? new Date(settings.terms_effective_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long' })
    : "January 2025";

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-blue/20 via-white to-bobo-green/20">
      {/* Header */}
      <section className="py-16 bg-gradient-to-r from-teedee-blue to-bobo-green text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center mb-6">
            <Button asChild variant="ghost" className="text-white hover:bg-white/20 mr-4">
              <Link to="/">
                <ArrowLeft className="h-5 w-5 mr-2" />
                Back to Home
              </Link>
            </Button>
          </div>
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <FileText className="h-16 w-16" />
            </div>
            <h1 className="text-5xl font-bold mb-6">Terms of Service</h1>
            <p className="text-xl max-w-3xl mx-auto opacity-90">
              Please read these terms carefully before using {siteTitle} services.
            </p>
            <p className="text-sm mt-4 opacity-75">Last updated: {effectiveDate}</p>
          </div>
        </div>
      </section>

      {/* Terms Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Card className="shadow-xl bg-white/90 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-2xl text-teedee-blue">Terms of Service for {siteTitle}</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-lg max-w-none space-y-8">
            <section>
              <h2 className="text-xl font-bold text-teedee-blue mb-4">1. Acceptance of Terms</h2>
              <p className="text-gray-700 leading-relaxed">
                Welcome to {siteTitle}! By accessing and using our website, mobile applications, educational content, and services, you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use our services.
              </p>
              <p className="text-gray-700 leading-relaxed">
                These Terms apply to all visitors, users, and others who access or use our services, including parents, guardians, educators, and children.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-bobo-green mb-4">2. Description of Service</h2>
              <p className="text-gray-700 leading-relaxed">
                {siteTitle} provides educational entertainment content including:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Animated episodes featuring educational content about Cayman Islands culture</li>
                <li>Printable educational activities and worksheets</li>
                <li>Interactive online games and activities</li>
                <li>Newsletter subscriptions and community engagement</li>
                <li>Educational resources for parents and teachers</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-brand-purple mb-4">3. Age Requirements and Parental Consent</h2>
              <p className="text-gray-700 leading-relaxed">
                Our services are designed for children under 13 with parental supervision. We comply with COPPA (Children's Online Privacy Protection Act) requirements:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Children under 13 must have parental permission to use our services</li>
                <li>Parents are responsible for supervising their child's use of our platform</li>
                <li>We do not knowingly collect personal information from children under 13 without parental consent</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-golden-orange mb-4">4. User Conduct</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Users of {siteTitle} services agree to:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Use our services for lawful, educational purposes only</li>
                <li>Respect the intellectual property rights of {siteTitle}</li>
                <li>Not attempt to hack, disrupt, or damage our services</li>
                <li>Not share inappropriate content or engage in harmful behavior</li>
                <li>Follow community guidelines in any interactive features</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-teedee-blue mb-4">5. Intellectual Property Rights</h2>
              <p className="text-gray-700 leading-relaxed">
                All content on {siteTitle} platforms, including but not limited to videos, images, characters, stories, activities, and educational materials, are protected by copyright and trademark laws. The {siteTitle} name and characters are registered trademarks.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Educational use of our printable activities in classrooms, homeschools, and non-commercial educational settings is permitted. Commercial use requires written permission.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-bobo-green mb-4">6. Educational Content Disclaimer</h2>
              <p className="text-gray-700 leading-relaxed">
                While we strive to provide accurate and age-appropriate educational content, {siteTitle} content is designed for entertainment and general educational purposes. Our content should supplement, not replace, formal education and parental guidance.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-brand-purple mb-4">7. Privacy and Data Protection</h2>
              <p className="text-gray-700 leading-relaxed">
                Your privacy is important to us. Our collection and use of personal information is governed by our Privacy Policy, which is incorporated into these Terms by reference. Please review our Privacy Policy to understand our practices.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-golden-orange mb-4">8. Third-Party Services</h2>
              <p className="text-gray-700 leading-relaxed">
                Our platform may contain links to third-party websites and services (such as YouTube, social media platforms). We are not responsible for the content, privacy policies, or practices of these third-party services.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-teedee-blue mb-4">9. Limitation of Liability</h2>
              <p className="text-gray-700 leading-relaxed">
                {siteTitle} and its creators provide services "as is" without warranties of any kind. We shall not be liable for any indirect, incidental, special, or consequential damages arising from the use of our services.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-bobo-green mb-4">10. Modifications to Terms</h2>
              <p className="text-gray-700 leading-relaxed">
                We may update these Terms from time to time. Significant changes will be communicated through our website or newsletter. Continued use of our services after changes constitutes acceptance of the new Terms.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-brand-purple mb-4">11. Governing Law</h2>
              <p className="text-gray-700 leading-relaxed">
                These Terms are governed by the laws of the Cayman Islands. Any disputes will be resolved in the courts of the Cayman Islands.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-golden-orange mb-4">12. Contact Information</h2>
              <DynamicContactSection title="these Terms of Service" />
            </section>

            <div className="border-t pt-8 mt-12">
              <p className="text-sm text-gray-500 text-center">
                © 2025 {siteTitle}. All rights reserved.<br />
                These Terms of Service are effective as of {effectiveDate}.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Terms;
